import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  api,
  AnalyticsDashboardResponse,
  AnalyticsProjectStat,
  MaterialEngagementResponse,
  ProjectAnalyticsTopUserRow,
} from '../../services/api';
import OverviewDashboardPanel from './OverviewDashboardPanel';
import {
  DateRangeSelector,
  DateRangeState,
  DateRangePreset,
  formatInputDate,
  parseInputDate,
  isBeforeDate,
  formatDisplayRange,
  calendarToday,
  DEFAULT_DATE_RANGE_PRESETS,
} from './DateRangeSelector';
import { formatCount } from './analyticsChartUi';

type OverviewGranularity = 'day' | 'month';
type DrillSortField = 'ceaNumber' | 'name' | 'mobile' | 'viewsAllTime' | 'viewsPeriod';
type SortDirection = 'asc' | 'desc';

const ANALYTICS_ALL_TIME_START = '2026-03-01';

function createAllTimeRange(): DateRangeState {
  const today = calendarToday();
  const start = parseInputDate(ANALYTICS_ALL_TIME_START);
  const end = start && isBeforeDate(today, start) ? start : today;
  return {
    startDate: ANALYTICS_ALL_TIME_START,
    endDate: formatInputDate(end),
  };
}

const ANALYTICS_DATE_PRESETS: DateRangePreset[] = [
  ...DEFAULT_DATE_RANGE_PRESETS,
  { id: 'allTime', label: 'All time', build: () => createAllTimeRange() },
];

interface ProjectEssentialsAnalyticsDialogProps {
  open: boolean;
  onClose: () => void;
}

const ProjectEssentialsAnalyticsDialog: React.FC<ProjectEssentialsAnalyticsDialogProps> = ({
  open,
  onClose,
}) => {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [overviewRange, setOverviewRange] = useState<DateRangeState>(() => createAllTimeRange());
  const [overviewGranularity, setOverviewGranularity] = useState<OverviewGranularity>('day');
  const [overviewData, setOverviewData] = useState<AnalyticsDashboardResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');
  const [materialData, setMaterialData] = useState<MaterialEngagementResponse | null>(null);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialError, setMaterialError] = useState('');
  const [materialMetric, setMaterialMetric] = useState<'previews' | 'downloads'>('previews');
  const [usersOverTimeOpen, setUsersOverTimeOpen] = useState(false);

  const [drillDownProject, setDrillDownProject] = useState<AnalyticsProjectStat | null>(null);
  const [drillDownUsers, setDrillDownUsers] = useState<ProjectAnalyticsTopUserRow[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownFilter, setDrillDownFilter] = useState('');
  const [drillDownSortField, setDrillDownSortField] = useState<DrillSortField>('viewsPeriod');
  const [drillDownSortDir, setDrillDownSortDir] = useState<SortDirection>('desc');
  const chartGridStroke = muiTheme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.14)'
    : 'rgba(31,41,68,0.12)';
  const chartAxisStroke = muiTheme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.3)'
    : 'rgba(31,41,68,0.24)';
  const chartTickStyle = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 12 }),
    [muiTheme.palette.text.secondary],
  );

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError('');
    try {
      const response = await api.getAdminProjectEssentialDashboard({
        startDate: overviewRange.startDate,
        endDate: overviewRange.endDate,
        granularity: overviewGranularity,
      });
      setOverviewData(response);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load Project Essentials analytics';
      setOverviewError(message);
      setOverviewData(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [overviewRange.endDate, overviewRange.startDate, overviewGranularity]);

  const fetchMaterial = useCallback(async () => {
    setMaterialLoading(true);
    setMaterialError('');
    try {
      const response = await api.getAdminMaterialEngagement({
        startDate: overviewRange.startDate,
        endDate: overviewRange.endDate,
      });
      setMaterialData(response);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load material activity';
      setMaterialError(message);
      setMaterialData(null);
    } finally {
      setMaterialLoading(false);
    }
  }, [overviewRange.endDate, overviewRange.startDate]);

  const fetchPanel = useCallback(async () => {
    await Promise.all([fetchOverview(), fetchMaterial()]);
  }, [fetchOverview, fetchMaterial]);

  useEffect(() => {
    if (!open) return;
    void fetchPanel();
  }, [open, fetchPanel]);

  const fetchDrillDown = useCallback(
    async (project: AnalyticsProjectStat) => {
      setDrillDownLoading(true);
      try {
        const response = await api.getAdminProjectEssentialProjectAnalytics({
          projectName: project.projectName,
          startDate: overviewRange.startDate,
          endDate: overviewRange.endDate,
        });
        setDrillDownUsers(response.topUsers);
      } catch {
        setDrillDownUsers([]);
      } finally {
        setDrillDownLoading(false);
      }
    },
    [overviewRange.endDate, overviewRange.startDate],
  );

  useEffect(() => {
    if (!drillDownProject) return;
    void fetchDrillDown(drillDownProject);
  }, [drillDownProject, fetchDrillDown]);

  const handleBarClick = useCallback((data: AnalyticsProjectStat) => {
    setDrillDownProject(data);
    setDrillDownFilter('');
    setDrillDownSortField('viewsPeriod');
    setDrillDownSortDir('desc');
  }, []);

  const isMonthlyOverview = overviewData?.granularity === 'month';

  const topProject = useMemo<AnalyticsProjectStat | null>(() => {
    if (!overviewData || overviewData.topProjects.length === 0) return null;
    return overviewData.topProjects[0];
  }, [overviewData]);

  const overviewUsersSeries = useMemo(() => {
    const uniqueSeries = overviewData?.uniqueUserSeries ?? [];
    const newUserSeries = overviewData?.signUpSeries ?? [];
    const byLabel = new Map<string, { label: string; uniqueUsers: number; newUsers: number }>();

    for (const point of uniqueSeries) {
      byLabel.set(point.label, {
        label: point.label,
        uniqueUsers: Number(point.users || 0),
        newUsers: byLabel.get(point.label)?.newUsers ?? 0,
      });
    }
    for (const point of newUserSeries) {
      const existing = byLabel.get(point.label);
      byLabel.set(point.label, {
        label: point.label,
        uniqueUsers: existing?.uniqueUsers ?? 0,
        newUsers: Number(point.users || 0),
      });
    }

    const ordered = Array.from(byLabel.values()).sort((a, b) => a.label.localeCompare(b.label));
    let runningNewUsers = 0;
    return ordered.map((point) => {
      runningNewUsers += point.newUsers;
      return { ...point, cumulativeNewUsers: runningNewUsers };
    });
  }, [overviewData]);

  const overviewActivitySeries = useMemo(() => {
    const series = overviewData?.activitySeries ?? [];
    const ordered = [...series].sort((a, b) => a.label.localeCompare(b.label));
    let running = 0;
    return ordered.map((point) => {
      const activities = Number(point.activities || 0);
      running += activities;
      return { label: point.label, activities, cumulativeActivities: running };
    });
  }, [overviewData]);

  const filteredSortedDrillDownUsers = useMemo(() => {
    let rows = drillDownUsers;
    if (drillDownFilter) {
      const lower = drillDownFilter.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.ceaNumber.toLowerCase().includes(lower) ||
          (r.name || '').toLowerCase().includes(lower) ||
          (r.mobile || '').toLowerCase().includes(lower),
      );
    }
    return [...rows].sort((a, b) => {
      let cmp = 0;
      switch (drillDownSortField) {
        case 'ceaNumber':
          cmp = a.ceaNumber.localeCompare(b.ceaNumber);
          break;
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '');
          break;
        case 'mobile':
          cmp = (a.mobile || '').localeCompare(b.mobile || '');
          break;
        case 'viewsAllTime':
          cmp = a.viewsAllTime - b.viewsAllTime;
          break;
        case 'viewsPeriod':
          cmp = a.viewsPeriod - b.viewsPeriod;
          break;
      }
      return drillDownSortDir === 'asc' ? cmp : -cmp;
    });
  }, [
    drillDownUsers,
    drillDownFilter,
    drillDownSortField,
    drillDownSortDir,
  ]);

  const toggleDrillSort = (field: DrillSortField) => {
    setDrillDownSortDir((prev) =>
      drillDownSortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc',
    );
    setDrillDownSortField(field);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
            minHeight: fullScreen ? undefined : '78vh',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'var(--color-text-strong)' }}>
                Project Essentials Analytics
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.78)' }}>
                Usage overview, trends, and project drill-down for Project Essentials.
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'var(--color-text-strong)' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <DateRangeSelector
                label="Overview Range"
                range={overviewRange}
                onApply={setOverviewRange}
                presets={ANALYTICS_DATE_PRESETS}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={overviewGranularity}
                onChange={(_event, value: OverviewGranularity | null) => {
                  if (!value) return;
                  setOverviewGranularity(value);
                  if (value === 'month') setUsersOverTimeOpen(true);
                }}
                aria-label="Series granularity"
                sx={{
                  height: 40,
                  '& .MuiToggleButton-root': {
                    px: 2,
                    textTransform: 'none',
                    borderColor: 'rgba(var(--color-text-rgb), 0.18)',
                    color: 'rgba(var(--color-text-rgb), 0.7)',
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    color: 'var(--color-accent)',
                    bgcolor: 'rgba(237,137,54,0.14)',
                    '&:hover': { bgcolor: 'rgba(237,137,54,0.2)' },
                  },
                }}
              >
                <ToggleButton value="day">Daily</ToggleButton>
                <ToggleButton value="month">Month on Month</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => void fetchPanel()}
                sx={{ borderColor: 'rgba(var(--color-text-rgb), 0.18)', color: 'rgba(var(--color-text-rgb), 0.88)' }}
              >
                Refresh
              </Button>
            </Box>

            <OverviewDashboardPanel
              data={overviewData}
              loading={overviewLoading}
              error={overviewError}
              usersSeries={overviewUsersSeries}
              activitySeries={overviewActivitySeries}
              topProject={topProject}
              isMonthlyOverview={isMonthlyOverview}
              usersOverTimeOpen={usersOverTimeOpen}
              onUsersOverTimeToggle={() => setUsersOverTimeOpen((v) => !v)}
              onBarClick={handleBarClick}
              copy={{
                uniqueUsersSubtitle: 'Agents with Project Essentials activity',
                newUsersSubtitle: 'First Project Essentials activity in period',
                topProjectEmptyText: 'No tagged EDM/eBook activity in this period.',
              }}
              chartGridStroke={chartGridStroke}
              chartAxisStroke={chartAxisStroke}
              chartTickStyle={chartTickStyle}
              enableChartDownload
              materialActivity={{
                loading: materialLoading,
                error: materialError,
                series: materialData?.series ?? [],
                metric: materialMetric,
                onMetricChange: setMaterialMetric,
                onRefresh: () => void fetchMaterial(),
              }}
            />
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!drillDownProject}
        onClose={() => setDrillDownProject(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'var(--color-text-strong)' }}>
                {drillDownProject?.projectName ?? 'Project'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', display: 'block', mt: 0.5 }}>
                Project Essentials activity (EDM/eBook + project page clicks). Overview range:{' '}
                {formatDisplayRange(overviewRange)}
              </Typography>
            </Box>
            <IconButton onClick={() => setDrillDownProject(null)} size="small" sx={{ mt: -0.5, color: 'var(--color-text-strong)' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              placeholder="Filter by CEA, name, or mobile…"
              value={drillDownFilter}
              onChange={(e) => setDrillDownFilter(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(var(--color-text-rgb), 0.45)', fontSize: 20 }} />,
              }}
              fullWidth
            />
            {drillDownLoading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {(
                        [
                          { field: 'ceaNumber' as const, label: 'CEA Number', align: 'left' as const },
                          { field: 'name' as const, label: 'Name', align: 'left' as const },
                          { field: 'mobile' as const, label: 'Mobile', align: 'left' as const },
                          { field: 'viewsAllTime' as const, label: 'Interactions (all time)', align: 'right' as const },
                          { field: 'viewsPeriod' as const, label: 'Interactions (selected period)', align: 'right' as const },
                        ] as const
                      ).map((col) => (
                        <TableCell
                          key={col.field}
                          align={col.align}
                          sx={{
                            fontWeight: 600,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                          onClick={() => toggleDrillSort(col.field)}
                        >
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            {col.label}
                            {drillDownSortField === col.field &&
                              (drillDownSortDir === 'asc' ? (
                                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                              ))}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSortedDrillDownUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                          {drillDownFilter
                            ? 'No results matching your filter.'
                            : 'No Project Essentials activity for this project.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSortedDrillDownUsers.map((row) => (
                        <TableRow key={row.ceaNumber}>
                          <TableCell>{row.ceaNumber}</TableCell>
                          <TableCell>{row.name?.trim() ? row.name : '—'}</TableCell>
                          <TableCell>{row.mobile?.trim() ? row.mobile : '—'}</TableCell>
                          <TableCell align="right">{formatCount(row.viewsAllTime)}</TableCell>
                          <TableCell align="right">{formatCount(row.viewsPeriod)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectEssentialsAnalyticsDialog;
